/**
 * Wraps `window.opener.postMessage` with envelope construction +
 * origin validation so each flow can call a single `reply(...)` and
 * not worry about envelope shape or target-origin handling.
 *
 * Origin validation is the only thing standing between a malicious
 * partner site and the auth page returning tokens to an attacker.
 * The auth page MUST only `postMessage` to the origin the partner
 * SDK declared in the `?origin=` query param, and that origin
 * MUST be validated against an allow-list before we send the token.
 *
 * There are now two tiers, because the two credentials have very
 * different blast radii.
 *
 * RENDERING + ID TOKEN — "any HTTPS origin" plus `http://localhost*`
 * for partner local development. Unchanged. An ID token expires in an
 * hour, and tightening this would break every partner not yet
 * registered.
 *
 * REFRESH TOKEN — the app's registered `allowedOrigins`, fetched from
 * `/apps/{appId}/policy`, exact-match only. A refresh token does not
 * expire, so an attacker who spawns a popup pointing at their own
 * origin and tricks a victim through the flow would convert a
 * one-hour window into permanent account access. Withholding the
 * refresh token from an unregistered origin costs that origin
 * nothing it has today; it simply keeps the old one-hour session.
 */

import {
  HORUS_AUTH_CHANNEL,
  HORUS_AUTH_PROTOCOL_VERSION,
  HorusAuthMessageBody,
  HorusAuthMessageEnvelope,
} from './protocol';

export interface PostMessageContext {
  /** Origin the SDK declared via `?origin=`. Validated before any post. */
  partnerOrigin: string;
  /** Echo of the SDK's `?state=` so the SDK can correlate concurrent flows. */
  state?: string;
  /**
   * Whether this origin may receive a refresh token, i.e. whether it is on
   * the app's registered `allowedOrigins`. False for every origin we merely
   * tolerate, and false whenever the policy could not be read at all.
   *
   * Optional so that absent means denied: a context built without deciding
   * this question withholds the refresh token rather than leaking one.
   */
  refreshAllowed?: boolean;
}

/**
 * Build the reply context.
 *
 * `allowedOrigins` comes from `/apps/{appId}/policy`. It is a separate,
 * stricter list than `isAcceptableOrigin` and gates only the refresh token.
 * It defaults to empty — omit it and no origin is trusted with a refresh
 * token, which is the safe direction for a caller that forgot to pass it.
 */
export function readContextFromUrl(
  allowedOrigins: readonly string[] = []
): PostMessageContext | null {
  const params = new URLSearchParams(window.location.search);
  const origin = params.get('origin');
  if (!origin || !isAcceptableOrigin(origin)) return null;
  const state = params.get('state') ?? undefined;
  return { partnerOrigin: origin, state, refreshAllowed: isRegistered(origin, allowedOrigins) };
}

/**
 * Exact string match against the registered list, after normalising each
 * side to a bare origin.
 *
 * Deliberately not a suffix or hostname test: `https://evil-soupgo.com`
 * must not pass because `https://soupgo.com` is registered, and
 * `https://soupgo.com.evil.com` must not pass either. No wildcard is
 * honoured here even if one somehow reaches the client — the policy
 * endpoint already strips `'*'`, and this is the second of the two locks.
 */
export function isRegistered(origin: string, allowedOrigins: readonly string[]): boolean {
  const normalised = normaliseOrigin(origin);
  if (!normalised) return false;
  return allowedOrigins.some((candidate) => normaliseOrigin(candidate) === normalised);
}

function normaliseOrigin(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

export function reply(ctx: PostMessageContext, body: HorusAuthMessageBody): void {
  const envelope: HorusAuthMessageEnvelope = {
    channel: HORUS_AUTH_CHANNEL,
    version: HORUS_AUTH_PROTOCOL_VERSION,
    state: ctx.state,
    body,
  };
  // Two transports, same envelope. Whichever target is available wins
  // — partners using the popup flow have `window.opener`; partners
  // using the inline `<HorusAuthModal>` iframe flow have `window.parent`
  // (and `window.parent !== window`). Orphan pages (no opener, top-
  // level frame) are silently ignored — the page should never be
  // reachable that way, and posting to nothing is safer than guessing.
  //
  // Crucially: pin the targetOrigin to the validated partner origin in
  // both branches. Posting to "*" would leak tokens to whatever
  // happens to be listening.
  if (window.opener) {
    window.opener.postMessage(envelope, ctx.partnerOrigin);
    return;
  }
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(envelope, ctx.partnerOrigin);
    return;
  }
}

/**
 * v0.1 allow-list. Tightening to the per-app `allowedOrigins` from
 * the backend is a v1 task — until then we accept any HTTPS origin
 * and localhost for development.
 */
function isAcceptableOrigin(origin: string): boolean {
  try {
    const u = new URL(origin);
    if (u.protocol === 'https:') return true;
    if (
      u.protocol === 'http:' &&
      (u.hostname === 'localhost' || u.hostname === '127.0.0.1')
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}
